<script setup lang="ts">
const emit = defineEmits(["offerHandled"]);
const props = defineProps({
  offer: {
    type: Object as PropType<Offer>,
    required: true,
  },
  collectible: {
    type: Object as PropType<Collectible>,
    required: true,
  },
});

const userStore = useUserStore();
const { trackEvent } = useGAnalytics();
const isModalOfferHandleShown = ref<boolean>(false);
const action = ref<"accept" | "reject" | "revoke">("accept");

const offerTitle = computed(() => {
  if (userStore.isCurrentUserId(props.collectible.gfalId)) {
    return "main.offers.youReceivedOffer";
  } else {
    return "main.offers.youMadeOffer";
  }
});

const offerAmountText = computed(() => {
  if (userStore.isCurrentUserId(props.collectible.gfalId)) {
    return "main.offers.highestOffer";
  } else {
    return "main.offers.yourOffer";
  }
});

const acceptOffer = () => {
  trackEvent("accept_offer_collectible_page");
  action.value = "accept";
  isModalOfferHandleShown.value = true;
};

const rejectOffer = () => {
  trackEvent("reject_offer_collectible_page");
  action.value = "reject";
  isModalOfferHandleShown.value = true;
};

const revokeOffer = () => {
  trackEvent("revoke_offer_collectible_page");
  action.value = "revoke";
  isModalOfferHandleShown.value = true;
};

const handleModalClose = () => {
  isModalOfferHandleShown.value = false;
  emit("offerHandled");
};
</script>
<template>
  <div
    class="my-8 bg-primary-900 p-3 rounded-sm flex flex-col xl:flex-row justify-between items-center gap-y-4">
    <div class="flex flex-col xl:flex-row items-center gap-2">
      <BaseIconOfferStamp />
      <p class="text-neutral-200 text-xl font-bold text-center">
        {{ $t(offerTitle) }}
      </p>
    </div>
    <div class="flex flex-col xl:flex-row gap-x-3 items-center">
      <p class="text-neutral-200 font-semibold">{{ $t(offerAmountText) }}</p>
      <div class="flex items-center gap-2">
        <BaseIcon
          name="GFAL_token"
          class="w-8"
          :alt="$t('main.imageAltTexts.GFALTokenLogo')" />
        <p class="text-neutral-200 font-bold text-lg">
          {{ roundNum(offer.amount.GFAL) }}
        </p>
        <p class="text-neutral-300">~ ${{ roundNum(offer.amount.USD) }}</p>
      </div>
    </div>
    <div
      v-if="!userStore.isCurrentUserId(offer.buyer)"
      class="flex flex-wrap justify-center items-center gap-4">
      <BaseButtonSubmit
        :text="$t('main.offers.accept')"
        size="sm"
        class="hidden xl:block"
        data-test="button-accept-offer-banner-sm"
        @click="acceptOffer" />
      <BaseButtonSubmit
        :text="$t('main.offers.accept')"
        size="long"
        class="xl:hidden"
        data-test="button-accept-offer-banner-long"
        @click="acceptOffer" />
      <BaseButtonCancel
        :text="$t('main.offers.reject')"
        size="sm"
        class="hidden xl:block"
        data-test="button-reject-offer-banner-sm"
        @click="rejectOffer" />
      <BaseButtonCancel
        :text="$t('main.offers.reject')"
        size="long"
        class="xl:hidden"
        data-test="button-reject-offer-banner-long"
        @click="rejectOffer" />
    </div>
    <div v-else>
      <BaseButtonSubmit
        :text="$t('main.offers.revokeOffer')"
        size="sm"
        class="hidden xl:block"
        data-test="button-revoke-offer-banner-sm"
        @click="revokeOffer" />
      <BaseButtonSubmit
        :text="$t('main.offers.revokeOffer')"
        size="long"
        class="xl:hidden"
        data-test="button-revoke-offer-banner-long"
        @click="revokeOffer" />
    </div>
  </div>
  <ModalOfferHandle
    v-if="isModalOfferHandleShown"
    :offer="offer"
    :action="action"
    @close="handleModalClose" />
</template>
